import React, { createContext, useContext, useState, useEffect } from "react";
import { useAuth } from "../hooks/useAuth";
import { useLoadUser } from "../Utils/customHooks/useLoadUser";
import { useQuery } from "../Utils/query";
import { fetchCreatorSlugFromDomainMappings } from "../Utils/url/fetchCreatorSlugFromDomainMappings";
import {
  getCreatorSlugFromHost,
  isNativeCreatorSubdomainHost,
} from "../Utils/url/getCreatorSlugFromHost";
import {
  isBrowsablePlatformApexHost,
  isLocalDevHost,
  isVercelDeploymentHost,
} from "../Utils/url/creatorHost";
import { readCachedCreatorPublicSurface } from "../Utils/url/creatorPublicSurface";
import {
  fetchWebsiteTemplateBootstrapCached,
  websiteTemplateBootstrapQueryKey,
} from "../Screens/DashboardView/MasterTemplateEditor/websiteTemplateBootstrap";

function normalizeDesignTokens<T extends Record<string, any>>(raw?: T): T | undefined {
  if (!raw) return raw;
  const templateName = (raw.template_name ?? raw.templateName)?.toLowerCase();
  const buttonText = raw.button_text ?? raw.tertiaryColor;
  const buttonBg = raw.button_background ?? raw.secondaryColor;

  if (templateName === "default" && (buttonText === "#000000" || buttonText === "#00000")) {
    return {
      ...raw,
      button_background: buttonText,
      button_text: buttonBg ?? "#e6e7eb",
    };
  }
  return raw;
}

export interface DesignContextType {
  fontFamily: string;
  primaryFont: string;
  secondaryFont: string;
  backgroundStyle: string;
  buttonBackground: string;
  buttonText: string;
  buttonBorder: string;
  textColor: string;
  socialIconColor: string;
  templateName: string;
  /** c4 — page background */
  primaryColor: string;
  /** c1 — buttons, borders, section titles */
  secondaryColor: string;
  /** semantic — button label text */
  tertiaryColor: string;
  forthColor: string,
  updateDesign: (
    primaryFont: string,
    secondaryFont: string,
    font: string,
    background: string,
    buttonBackground: string,
    buttonText: string,
    buttonBorder: string,
    textColor: string,
    socialIconColor: string,
    templateName: string,
    cardColor?: string,
  ) => void;
}
const DesignContext = createContext<DesignContextType | undefined>(undefined);
/** First path segments that are app routes, not public creator usernames (e.g. /dashboard/design was read as username "dashboard"). */
const NON_CREATOR_PATH_ROOTS = new Set([
  "dashboard",
  "login",
  "signup",
  "onboarding",
  "website-onboarding",
  "website-onboarding-major",
  "website-onboarding-full",
  "tutorials",
  "googlecallback",
  "auth",
  "login-email",
  "login-admin",
  "subscribe",
  "subscribe-website",
  "pricing",
  "payment-status",
  "payment-canceled",
  "add-team-payment-success",
  "add-member",
  "how-it-works",
  "accept-invite",
  "loading",
  "global",
  "no-user-found",
  "404",
  "401",
  "masterclass",
  "privacy",
  "order",
  "checkout",
  "product-checkout",
  "cart",
  "payment-success",
  "stripe",
  "payment-cancel",
  "onboarding-success",
  "private-beta",
  "embeddemo",
  "website",
]);

function getParamId(): string | undefined {
  const path = window.location.pathname;
  // Remove empty segments
  const parts = path.split("/").filter(Boolean); // e.g. ['embed','username'] or ['username']

  if (parts.length === 0) return undefined;

  const root = parts[0].toLowerCase();

  // Handle /embed/:username, /preview/:username, and /website/:username
  if (root === "embed" || root === "preview" || root === "website") {
    return parts[1]?.toLowerCase();
  }

  if (root === "loading" && parts[1]) {
    return parts[1].toLowerCase();
  }

  if (NON_CREATOR_PATH_ROOTS.has(root)) {
    return undefined;
  }

  // Default to first segment as username: /:username or /:username/:id
  return root;
}

export const DesignProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { username: authUsername } = useAuth();

  const paramUsername = getParamId();

  const hostname =
    typeof window !== "undefined" ? window.location.hostname : "";
  const slugFromHost =
    typeof window !== "undefined"
      ? getCreatorSlugFromHost(hostname)
      : undefined;
  const needsDomainLookup =
    typeof window !== "undefined" &&
    !isLocalDevHost(hostname) &&
    !isVercelDeploymentHost(hostname) &&
    !isNativeCreatorSubdomainHost(hostname) &&
    !isBrowsablePlatformApexHost(hostname);

  const [username, setUsername] = useState<string | undefined>(
    slugFromHost ?? (needsDomainLookup ? undefined : paramUsername),
  );
  const [isResolvingDomain, setIsResolvingDomain] = useState(needsDomainLookup);

  // Resolve username from custom domain if pathname method didn't work
  useEffect(() => {
    const resolveUsername = async () => {
      const pathname = window.location.pathname;
      // Dashboard: creator is whoever is selected in auth (creator / admin impersonation / member), never the path.
      if (pathname.startsWith("/dashboard")) {
        setIsResolvingDomain(false);
        const u = authUsername?.trim().toLowerCase();
        setUsername(u || undefined);
        return;
      }

      const host = window.location.hostname;
      const slugFromHostNow = getCreatorSlugFromHost(host);

      if (isNativeCreatorSubdomainHost(host) && slugFromHostNow) {
        setUsername(slugFromHostNow);
        setIsResolvingDomain(false);
        return;
      }

      if (isLocalDevHost(host) || isBrowsablePlatformApexHost(host) || isVercelDeploymentHost(host)) {
        setIsResolvingDomain(false);
        setUsername(paramUsername);
        return;
      }

      setIsResolvingDomain(true);
      try {
        const slug = await fetchCreatorSlugFromDomainMappings(host);
        if (slug) {
          setUsername(slug);
        } else {
          console.warn(
            "[DesignContext] Could not resolve creator from domain:",
            host,
          );
          setUsername(undefined);
        }
      } catch (error) {
        console.error("[DesignContext] Error resolving domain:", error);
        setUsername(undefined);
      } finally {
        setIsResolvingDomain(false);
      }
    };

    resolveUsername();
  }, [paramUsername, authUsername]);

  // Seed initial state from localStorage to avoid a blank first paint.
  // Only reads from the per-user `design:<username>` key so that one creator's
  // saved theme can never bleed into another creator's public page.
  const readCachedDesign = (usernameToCheck?: string) => {
    try {
      const user = usernameToCheck || paramUsername || username;
      if (!user) return undefined as any;

      const raw = localStorage.getItem(`design:${user}`);
      return raw ? normalizeDesignTokens(JSON.parse(raw)) : undefined;
    } catch {
      return undefined as any;
    }
  };
  const cached = readCachedDesign();
  // Website public pages already fetch user via bootstrap — a parallel
  // useLoadUser(select *) doubles the cold Slow 4G RTT and contends with
  // template_settings / template_components. Skip until we know the surface
  // is classic Link-in-Bio (or dashboard).
  const pathForDesign =
    typeof window !== "undefined" ? window.location.pathname || "" : "";
  const onDashboardPath = pathForDesign.startsWith("/dashboard");
  const handleForSurface = String(username || "").trim().toLowerCase();
  const cachedSurface = handleForSurface
    ? readCachedCreatorPublicSurface(handleForSurface)
    : null;
  const surfaceBootstrap = useQuery(
    websiteTemplateBootstrapQueryKey(handleForSurface || "__none__"),
    () => fetchWebsiteTemplateBootstrapCached(handleForSurface),
    {
      enabled: Boolean(handleForSurface) && !onDashboardPath,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  );
  const skipLibUserDesignFetch =
    pathForDesign.startsWith("/website") ||
    cachedSurface === "website" ||
    Boolean(
      surfaceBootstrap.data &&
        !surfaceBootstrap.data.userNotFound &&
        surfaceBootstrap.data.hasWebsiteSurface,
    ) ||
    // Wait for surface detect before racing a full user* on public URLs.
    (Boolean(handleForSurface) &&
      !onDashboardPath &&
      cachedSurface !== "link-in-bio" &&
      (surfaceBootstrap.isLoading || surfaceBootstrap.isFetching));
  const { data: userData }: any = useLoadUser(
    skipLibUserDesignFetch ? undefined : username,
  );
  const designTokens = normalizeDesignTokens(userData?.designTokens);
  // const username="sbcheck10"
  // const param = useParams()
  // const username = param.username
  const [fontFamily, setFontFamily] = useState(designTokens?.font_family  ?? cached?.font_family ?? "");
  const [primaryFont, setPrimaryFont] = useState(
    designTokens?.primary_font_family || cached?.primary_font_family || cached?.font_family || ""
  );
  const [secondaryFont, setSecondaryFont] = useState(
    designTokens?.secondary_font_family || cached?.secondary_font_family || cached?.font_family || ""
  );
  const [primaryColor, setPrimaryColor] = useState(
    designTokens?.background_style || cached?.background_style || ""
  );
  const [secondaryColor, setSecondaryColor] = useState(
    designTokens?.button_background || cached?.button_background || "#e6e7eb"
  );
  const [tertiaryColor, setTertiaryColor] = useState(
    designTokens?.button_text || cached?.button_text || ""
  );
  const [forthColor, setForthColor] = useState(
    designTokens?.card_style ?? designTokens?.forthColor ?? cached?.card_style ?? "#e6e7eb"
  )
  const [backgroundStyle, setBackgroundStyle] = useState(
    designTokens?.background_style || cached?.background_style || ""
  );
  const [buttonBackground, setButtonBackground] = useState(
    designTokens?.button_background || cached?.button_background || ""
  );
  const [buttonText, setButtonText] = useState(designTokens?.button_text || cached?.button_text || "");
  const [buttonBorder, setButtonBorder] = useState(designTokens?.button_border || cached?.button_border || "");
  const [textColor, setTextColor] = useState(designTokens?.text_color || cached?.text_color || "");
  const [socialIconColor, setSocialIconColor] = useState(
    designTokens?.social_icon_color || cached?.social_icon_color || ""
  );
  const [templateName, setTemplateName] = useState(
    designTokens?.template_name || cached?.template_name || ""
  );

  useEffect(() => {
    const loadUserDesign = async () => {
      // Don't load if we're still resolving the domain
      if (isResolvingDomain) {
        return;
      }

      if (username) {
  
        
        // Try to use cached design first for instant display
        // const cachedDesign = readCachedDesign(username);
        // if (cachedDesign) {
       
        //   setFontFamily(cachedDesign.font_family || "");
        //   setBackgroundStyle(cachedDesign.background_style || "");
        //   setButtonBackground(cachedDesign.button_background || "");
        //   setButtonText(cachedDesign.button_text || "");
        //   setButtonBorder(cachedDesign.button_border || "");
        //   setTextColor(cachedDesign.text_color || "");
        //   setSocialIconColor(cachedDesign.social_icon_color || "");
        //   setTemplateName(cachedDesign.template_name || "");
        //   setPrimaryColor(cachedDesign.background_style || "");
        //   setSecondaryColor(cachedDesign.button_background || "#e6e7eb");
        //   setTertiaryColor(cachedDesign.button_text || "");
        // }

        // Then fetch fresh data from server
        try {
          // const data = await getUserAndCheckExistence(username);
         
        
          
          // Extract design data - getUserAndCheckExistence returns { designTokens: {...}, ... }
          
          
          // Check if user exists and designTokens is available
          if (userData && designTokens && typeof designTokens === "object") {
            // Same token mapping for public page and /embed/:username — creator theme should match.
            setFontFamily(designTokens?.font_family || "");
            setPrimaryFont(designTokens?.primary_font_family || "");
            setSecondaryFont(designTokens?.secondary_font_family || "");
            setBackgroundStyle(designTokens?.background_style || "");
            setButtonBackground(designTokens?.button_background || "");
            setButtonText(designTokens?.button_text || "");
            setButtonBorder(designTokens?.button_border || "");
            setTextColor(designTokens?.text_color || "");
            setSocialIconColor(designTokens?.social_icon_color || "");
            setTemplateName(designTokens?.template_name || "");
            setPrimaryColor(designTokens?.background_style || "");
            setSecondaryColor(designTokens?.button_background || "#e6e7eb");
            setTertiaryColor(designTokens?.button_text || "");
            setForthColor(designTokens?.card_style ?? designTokens?.forthColor ?? "#e6e7eb");
            // Cache the design data for future use (use snake_case format for consistency)
            try {
              const cacheData = {
                ...designTokens,
                font_family: designTokens?.font_family,
                primary_font_family: designTokens?.primary_font_family,
                secondary_font_family: designTokens?.secondary_font_family,
                background_style: designTokens?.background_style,
                button_background: designTokens?.button_background,
                button_text: designTokens?.button_text,
                button_border: designTokens?.button_border,
                text_color: designTokens?.text_color,
                social_icon_color: designTokens?.social_icon_color,
                template_name: designTokens?.template_name,
                card_style: designTokens?.card_style ?? "#e6e7eb",
                forthColor: designTokens?.card_style ?? designTokens?.forthColor ?? "#e6e7eb",
              };
              localStorage.setItem(`designTokens:${username}`, JSON.stringify(cacheData));
              localStorage.setItem(`design:${username}`, JSON.stringify(cacheData));
            
            } catch (error) {
              console.error("Error caching design:", error);
            }
          } else if (userData && typeof userData === "object") {
            // Website-only creators often have no LiB designTokens — expected,
            // not a load failure. Only warn when a user row loaded without tokens.
            console.warn("[DesignContext] No design tokens found or invalid data:", { userData, designTokens });
            // Only clear if we don't have cached data
            // if (!cachedDesign) {
            //   setFontFamily("");
            //   setBackgroundStyle("");
            //   setButtonBackground("");
            //   setButtonText("");
            //   setButtonBorder("");
            //   setTextColor("");
            //   setSocialIconColor("");
            //   setTemplateName("");
            //   setPrimaryColor("");
            //   setSecondaryColor("");
            //   setTertiaryColor("");
            // }
          }
        } catch (error) {
          console.error("[DesignContext] Error loading user design:", error);
          // Keep cached design if available
          // if (!cachedDesign) {
          //   setFontFamily("");
          //   setBackgroundStyle("");
          //   setButtonBackground("");
          //   setButtonText("");
          //   setButtonBorder("");
          //   setTextColor("");
          //   setSocialIconColor("");
          //   setTemplateName("");
          //   setPrimaryColor("");
          //   setSecondaryColor("");
          //   setTertiaryColor("");
          // }
        }
      } else {
        // No username resolved, clear design
        setFontFamily("");
        setPrimaryFont("");
        setSecondaryFont("");
        setBackgroundStyle("");
        setButtonBackground("");
        setButtonText("");
        setButtonBorder("");
        setTextColor("");
        setSocialIconColor("");
        setTemplateName("");
        setPrimaryColor("");
        setSecondaryColor("");
        setTertiaryColor("");
      }
    };
    loadUserDesign();
  }, [username, isResolvingDomain, userData]);

  const updateDesign = (
    font: string,
    primFont: string,
    secFont: string,
    background: string,
    buttonBg: string,
    buttonTxt: string,
    buttonBdr: string,
    txtColor: string,
    socialColor: string,
    tmplName: string,
    cardColor?: string,
  ) => {
    setFontFamily(font);
    setPrimaryFont(primFont); 
    setSecondaryFont(secFont); 
    setBackgroundStyle(background);
    setButtonBackground(buttonBg);
    setButtonText(buttonTxt);
    setButtonBorder(buttonBdr);
    setTextColor(txtColor);
    setSocialIconColor(socialColor);
    setTemplateName(tmplName);
    setPrimaryColor(background);
    setSecondaryColor(buttonBg || "#e6e7eb");
    setTertiaryColor(buttonTxt);
    setForthColor(cardColor);
  };


  return (
    <DesignContext.Provider
      value={{
        fontFamily,
        primaryFont,
        secondaryFont,
        backgroundStyle,
        buttonBackground,
        buttonText,
        buttonBorder,
        textColor,
        socialIconColor,
        templateName,
        primaryColor,
        secondaryColor,
        tertiaryColor,
        forthColor,
        updateDesign,
      }}
    >
      {children}
    </DesignContext.Provider>
  );
};

const noopUpdateDesign: DesignContextType["updateDesign"] = () => {};

/**
 * Supplies DesignContext for subtrees that render StayUpToDate primitives (e.g.
 * RenderComponentLoader) without loading design from the user table — used by
 * the public website template where ThemeTokens already define the look.
 */
export function DesignStaticProvider({
  children,
  fontFamily = "ui-sans-serif, system-ui, sans-serif",
  primaryFont,
  secondaryFont,
  primaryColor = "#1a1a1a",
  secondaryColor = "#e6e7eb",
  tertiaryColor = "#333333",
  forthColor = "#e6e7eb",
  socialIconColor,
  templateName = "Default",
}: {
  children: React.ReactNode;
  fontFamily?: string;
  primaryFont?: string;
  secondaryFont?: string;
  primaryColor?: string;
  secondaryColor?: string;
  tertiaryColor?: string;
  forthColor?: string;
  socialIconColor?: string;
  templateName?: string;
}) {
  const headingFont = primaryFont || fontFamily;
  const bodyFont = secondaryFont || fontFamily;
  const iconColor = socialIconColor || tertiaryColor;
  const value: DesignContextType = React.useMemo(
    () => ({
      fontFamily: bodyFont,
      primaryFont: headingFont,
      secondaryFont: bodyFont,
      backgroundStyle: primaryColor,
      buttonBackground: secondaryColor,
      buttonText: tertiaryColor,
      buttonBorder: secondaryColor,
      textColor: tertiaryColor,
      socialIconColor: iconColor,
      templateName,
      primaryColor,
      secondaryColor,
      tertiaryColor,
      forthColor,
      updateDesign: noopUpdateDesign,
    }),
    [
      bodyFont,
      headingFont,
      primaryColor,
      secondaryColor,
      tertiaryColor,
      forthColor,
      iconColor,
      templateName,
    ],
  );
  return (
    <DesignContext.Provider value={value}>{children}</DesignContext.Provider>
  );
}

export const useDesign = () => {
  const context = useContext(DesignContext);
  if (context === undefined) {
    throw new Error("useDesign must be used within DesignProvider or DesignStaticProvider");
  }
  return context;
};
